/**
 * Collects ARIA/form-control state flags (selected, expanded, disabled,
 * checked, etc.) emitted alongside each role in the assistant page outline.
 * Extracted from `assistant-page-snapshot-describe.ts` so the describer stays
 * focused on role → line formatting.
 */
export const CHECKABLE_ROLES = new Set(["checkbox", "switch", "radio"])

export function collectStates(node: Element, role: string): string {
    const parts: string[] = []
    pushBooleanState(parts, node, "aria-selected", "selected")
    pushBooleanState(parts, node, "aria-expanded", "expanded")
    pushBooleanState(parts, node, "aria-pressed", "pressed")
    pushDisabled(parts, node)
    pushCurrent(parts, node)
    pushChecked(parts, node, role)
    pushTextInputFlags(parts, node, role)
    return parts.length > 0 ? `[${parts.join(",")}]` : ""
}

function pushBooleanState(
    parts: string[],
    node: Element,
    attribute: string,
    label: string
): void {
    if (node.getAttribute(attribute) === "true") parts.push(`${label}=true`)
}

function pushDisabled(parts: string[], node: Element): void {
    if (node.getAttribute("aria-disabled") === "true") {
        parts.push("disabled=true")
        return
    }
    // The native `disabled` boolean attribute is only meaningful on form
    // controls (<button>, <input>, <select>, <textarea>, <fieldset>, …), all
    // of which reflect it via the attribute.
    if (node.hasAttribute("disabled")) parts.push("disabled=true")
}

function pushCurrent(parts: string[], node: Element): void {
    const value = node.getAttribute("aria-current")
    if (value && value !== "false") parts.push(`current=${value}`)
}

function pushChecked(parts: string[], node: Element, role: string): void {
    if (!CHECKABLE_ROLES.has(role)) return
    const state = checkedStateFor(node)
    if (state !== null) parts.push(`checked=${state}`)
}

function checkedStateFor(node: Element): string | null {
    if (node instanceof HTMLInputElement) {
        if (node.indeterminate) return "mixed"
        return node.checked ? "true" : "false"
    }
    const aria = node.getAttribute("aria-checked")
    if (aria === "true" || aria === "false" || aria === "mixed") return aria
    return null
}

function pushTextInputFlags(
    parts: string[],
    node: Element,
    role: string
): void {
    if (role !== "textbox" && role !== "searchbox" && role !== "spinbutton") {
        return
    }
    if (
        node.hasAttribute("required") ||
        node.getAttribute("aria-required") === "true"
    ) {
        parts.push("required=true")
    }
    if (
        (node instanceof HTMLInputElement ||
            node instanceof HTMLTextAreaElement) &&
        node.readOnly
    ) {
        parts.push("readonly=true")
    }
}
